/**
 * VueLayers
 * Web map Vue components with the power of OpenLayers
 *
 * @package vuelayers
 * @author Vladimir Vershinin <ghettovoice@gmail.com>
 * @version 0.11.37
 * @license MIT
 * @copyright (c) 2017-2021, Vladimir Vershinin <ghettovoice@gmail.com>
 */
import _classCallCheck from '@babel/runtime-corejs3/helpers/esm/classCallCheck';
import _createClass from '@babel/runtime-corejs3/helpers/esm/createClass';
import _valuesInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/values';
import _Promise from '@babel/runtime-corejs3/core-js-stable/promise';
import Cluster from 'ol/source/Cluster';
import { interval } from 'rxjs/_esm5/internal/observable/interval';
import { first } from 'rxjs/_esm5/internal/operators/first';
import { map } from 'rxjs/_esm5/internal/operators/map';
import { skipWhile } from 'rxjs/_esm5/internal/operators/skipWhile';
import { ok, hasSource } from '../util/assert';
import _Object$keys from '@babel/runtime-corejs3/core-js-stable/object/keys';
import _Object$getOwnPropertySymbols from '@babel/runtime-corejs3/core-js-stable/object/get-own-property-symbols';
import _filterInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/filter';
import _Object$getOwnPropertyDescriptor from '@babel/runtime-corejs3/core-js-stable/object/get-own-property-descriptor';
import _Object$getOwnPropertyDescriptors from '@babel/runtime-corejs3/core-js-stable/object/get-own-property-descriptors';
import _defineProperty from '@babel/runtime-corejs3/helpers/esm/defineProperty';
import { makeWatchers } from '../util/vue-helpers';
import { sourceContainer, vectorSource } from '../mixin';
import { createPointGeom, findPointOnSurface } from '../ol-ext';
import { observableFromOlEvent } from '../rx-ext';
import mergeDescriptors from '../util/multi-merge-descriptors';
import '@babel/runtime-corejs3/core-js-stable/instance/concat';
import _Object$assign from '@babel/runtime-corejs3/core-js-stable/object/assign';
import { pick } from '../util/minilo';

var Builder = /*#__PURE__*/function () {
  function Builder() {
    _classCallCheck(this, Builder);
  }

  _createClass(Builder, [{
    key: "getSource",
    value: function getSource() {
      return this.source;
    }
    /**
     * @param {Vector|undefined} value
     * @returns {Builder}
     */

  }, {
    key: "setSource",
    value: function setSource(value) {
      this.source = value;
      return this;
    }
    /**
     * @param value
     * @returns {Builder}
     */

  }, {
    key: "setAttributions",
    value: function setAttributions(value) {
      this.attributions = value;
      return this;
    }
    /**
     * @param value
     * @returns {Builder}
     */

  }, {
    key: "setDistance",
    value: function setDistance(value) {
      this.distance = value;
      return this;
    }
    /**
     * @param value
     * @returns {Builder}
     */

  }, {
    key: "setGeometryFunction",
    value: function setGeometryFunction(value) {
      this.geometryFunction = value;
      return this;
    }
    /**
     * @param value
     * @returns {Builder}
     */

  }, {
    key: "setLogo",
    value: function setLogo(value) {
      this.logo = value;
      return this;
    }
    /**
     * @param value
     * @returns {Builder}
     */

  }, {
    key: "setProjection",
    value: function setProjection(value) {
      this.projection = value;
      return this;
    }
    /**
     * @param value
     * @returns {Builder}
     */

  }, {
    key: "setWrapX",
    value: function setWrapX(value) {
      this.wrapX = value;
      return this;
    }
    /**
     * @param key
     * @param value
     * @return {void}
     */

  }, {
    key: "set",
    value: function set(key, value) {
      _valuesInstanceProperty(this) || (this.values = {});
      _valuesInstanceProperty(this)[key] = value;
    }
    /**
     * @return {Cluster}
     */

  }, {
    key: "build",
    value: function build() {
      ok(this.source, 'source is provided');
      var source = new Cluster({
        attributions: this.attributions,
        distance: this.distance,
        geometryFunction: this.geometryFunction,
        logo: this.logo,
        projection: this.projection,
        source: this.source,
        wrapX: this.wrapX
      });
      source.setProperties(_valuesInstanceProperty(this));
      return source;
    }
    /**
     * @return {Promise<Cluster>}
     */

  }, {
    key: "promise",
    value: function promise() {
      var _this = this;

      return interval(100).pipe(skipWhile(function () {
        return !_this.source;
      }), first(), map(this.build.bind(this))).toPromise(_Promise);
    }
  }]);

  return Builder;
}();

function ownKeys(object, enumerableOnly) { var keys = _Object$keys(object); if (_Object$getOwnPropertySymbols) { var symbols = _Object$getOwnPropertySymbols(object); if (enumerableOnly) { symbols = _filterInstanceProperty(symbols).call(symbols, function (sym) { return _Object$getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (_Object$getOwnPropertyDescriptors) { Object.defineProperties(target, _Object$getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, _Object$getOwnPropertyDescriptor(source, key)); }); } } return target; }
var script = {
  name: 'vl-source-cluster',
  mixins: [vectorSource, sourceContainer],
  props: {
    distance: {
      type: Number,
      default: 20
    },

    /**
     * Geometry function factory
     * @type {function(): (function(f: ol.Feature): ol.geom.SimpleGeometry|undefined)} geomFuncFactory
     */
    geomFuncFactory: {
      type: Function,
      default: defaultGeomFuncFactory
    }
  },
  computed: {
    geomFunc: function geomFunc() {
      return this.geomFuncFactory();
    }
  },
  methods: {
    /**
     * @return {Promise<ol.source.Cluster>}
     * @protected
     */
    createSource: function createSource() {
      // partial setup of ol.source.Cluster with the help of SourceBuilder class

      /**
       * @type {SourceBuilder}
       * @private
       */
      this._sourceBuilder.setAttributions(this.attributions).setDistance(this.distance).setGeometryFunction(this.geomFunc).setLogo(this.logo).setProjection(this.projection).setWrapX(this.wrapX);

      return this._sourceBuilder.promise();
    },

    /**
     * @return {Object}
     * @protected
     */
    getServices: function getServices() {
      return mergeDescriptors(vectorSource.methods.getServices.call(this), sourceContainer.methods.getServices.call(this));
    },

    /**
     * @return {{
     *     setSource: function(ol.source.Source): void,
     *     getSource: function(): ol.source.Source
     *   }|undefined}
     * @protected
     */
    getSourceTarget: function getSourceTarget() {
      return this._sourceBuilder;
    },
    subscribeAll: function subscribeAll() {
      vectorSource.methods.subscribeAll.call(this);
      subscribeToSourceChanges.call(this);
    }
  },
  watch: _objectSpread({
    distance: function distance(value) {
      if (this.$source && value !== this.$source.getDistance()) {
        this.$source.setDistance(value);
      }
    }
  }, makeWatchers(['geomFunc'], function () {
    return function () {
      this.scheduleRecreate();
    };
  })),
  created: function created() {
    /**
     * @type {SourceBuilder}
     * @private
     */
    this._sourceBuilder = new Builder();
    Object.defineProperties(this, {
      $innerSource: {
        enumerable: true,
        get: this.getSource
      }
    });
  }
};
/**
 * @returns {function(f: ol.Feature): ol.geom.SimpleGeometry|undefined}
 */

function defaultGeomFuncFactory() {
  return function (feature) {
    var geometry = feature.getGeometry();
    if (!geometry) return;
    var coordinate = findPointOnSurface(geometry);

    if (coordinate) {
      return createPointGeom(coordinate);
    }
  };
}

function subscribeToSourceChanges() {
  var _this = this;

  hasSource(this);
  var adds = observableFromOlEvent(this.$source, 'addfeature');
  this.subscribeTo(adds, function (_ref) {
    var feature = _ref.feature;

    _this.addFeature(feature);
  });
  var removes = observableFromOlEvent(this.$source, 'removefeature');
  this.subscribeTo(removes, function (_ref2) {
    var feature = _ref2.feature;

    _this.removeFeature(feature);
  });
}

var __vue_script__ = script;
/* template */

/* style */

var __vue_inject_styles__ = undefined;
/* scoped */

var __vue_scope_id__ = undefined;
/* module identifier */

var __vue_module_identifier__ = undefined;
/* functional template */

var __vue_is_functional_template__ = undefined;
/* component normalizer */

function __vue_normalize__(template, style, script$$1, scope, functional, moduleIdentifier, createInjector, createInjectorSSR) {
  var component = (typeof script$$1 === 'function' ? script$$1.options : script$$1) || {}; // For security concerns, we use only base name in production mode.

  component.__file = "source.vue";

  if (!component.render) {
    component.render = template.render;
    component.staticRenderFns = template.staticRenderFns;
    component._compiled = true;
    if (functional) component.functional = true;
  }

  component._scopeId = scope;

  return component;
}
/* style inject */

/* style inject SSR */


var Source = __vue_normalize__({}, __vue_inject_styles__, __vue_script__, __vue_scope_id__, __vue_is_functional_template__, __vue_module_identifier__, undefined, undefined);

function plugin(Vue) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  if (plugin.installed) {
    return;
  }

  plugin.installed = true;
  options = pick(options, 'dataProjection');

  _Object$assign(Source, options);

  Vue.component(Source.name, Source);
}

export default plugin;
export { Source, plugin as install };
